import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { FaCheck } from "react-icons/fa";
import { FiArrowRight } from "react-icons/fi";
import { PackagesData } from "../data/PackagesData";
import CabinetProtectiaMunciiLogo from "../images/cabinetProtectiaMuncii.png";
import LatinBugLogo from "../images/latinBug.png";
import LoxanLogo from "../images/loxan.jpg";
import BizzLinkLogo from "../images/bizzLink.png";
import PresspectivaLogo from "../images/presspectiva.jpeg";
import UntoldLogo from "../images/untold.jpeg";
import CodruLogo from "../images/codru.png";
import DarcLogo from "../images/darc.png";
import CopyShopLogo from "../images/copyshop.jpeg";
import ZiuaDeVestLogo from "../images/ziuaDeVest.jpeg";
import LitensLogo from "../images/litens.jpeg";
import IuliusLogo from "../images/iulius.png";
import AvonLogo from "../images/avon.png";
import EndavaLogo from "../images/endava.png";
import SponsorModal from "./SponsorModal";

const Section = styled.section`
  background: linear-gradient(180deg, #fffcf2 0%, #fff8e1 100%);
  padding: 5rem 0;
`;

const Wrap = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const Eyebrow = styled.p`
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font-size: 0.85rem;
  color: #faa71e;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const Heading = styled.h2`
  font-size: clamp(1.8rem, 4vw, 2.6rem);
  color: #0a2a4a;
  font-weight: 800;
  text-align: center;
  margin-bottom: 0.5rem;
`;

const Sub = styled.p`
  text-align: center;
  color: #2d3e56;
  margin-bottom: 3rem;
  max-width: 720px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const CurrentSponsorsBlock = styled.div`
  margin-bottom: 4rem;
`;

const ThanksHeading = styled(Heading)`
  margin-bottom: 0.75rem;
`;

const ThanksSub = styled(Sub)`
  margin-bottom: 2rem;
`;

const scroll = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;

const Marquee = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  -webkit-mask-image: linear-gradient(
    to right,
    transparent 0,
    #000 80px,
    #000 calc(100% - 80px),
    transparent 100%
  );
  mask-image: linear-gradient(
    to right,
    transparent 0,
    #000 80px,
    #000 calc(100% - 80px),
    transparent 100%
  );

  &:hover .marquee-track,
  &:focus-within .marquee-track {
    animation-play-state: paused;
  }
`;

const Track = styled.div.attrs({ className: "marquee-track" })`
  display: flex;
  width: max-content;
  gap: 1.5rem;
  padding: 0.5rem 0.75rem;
  animation: ${scroll} 35s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    flex-wrap: wrap;
    justify-content: center;
    width: 100%;
  }
`;

const SponsorCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 1.25rem 1.5rem;
  width: 220px;
  height: 130px;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 14px 30px -18px rgba(10, 42, 74, 0.25);
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 16px 32px -16px rgba(10, 42, 74, 0.35);
  }

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const JoinCTA = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.75rem 1rem;
  margin-top: 2rem;
  text-align: center;
`;

const JoinText = styled.p`
  color: #2d3e56;
  font-size: 0.95rem;
  font-weight: 500;
  margin: 0;
`;

const JoinButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: #0a2a4a;
  font-weight: 800;
  font-size: 0.95rem;
  background: transparent;
  border: none;
  border-bottom: 2px solid #faa71e;
  padding: 2px 0;
  cursor: pointer;
  font-family: inherit;
  transition: color 0.2s ease, border-color 0.2s ease;

  &:hover {
    color: #faa71e;
  }

  svg {
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: translateX(3px);
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(10, 42, 74, 0.08);
  max-width: 720px;
  margin: 0 auto 3rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.5rem;
`;

const tierColors = {
  platinum: { bg: "#e7eef7", accent: "#0a2a4a", ribbon: "#0a2a4a" },
  gold: { bg: "#fff5d3", accent: "#caa42a", ribbon: "#faa71e" },
  silver: { bg: "#eef0f3", accent: "#717b89", ribbon: "#9aa3ad" },
  bronze: { bg: "#f5e4d3", accent: "#a26a3a", ribbon: "#c98856" },
  premii: { bg: "#fff0e6", accent: "#e85d04", ribbon: "#e85d04" },
};

const Card = styled.div`
  background: ${({ $tier }) => tierColors[$tier].bg};
  border-radius: 20px;
  padding: 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  position: relative;
  transition: transform 0.25s ease;
  box-shadow: 0 14px 30px -16px rgba(10, 42, 74, 0.2);

  &:hover {
    transform: translateY(-4px);
  }
`;

const Ribbon = styled.div`
  position: absolute;
  top: -12px;
  left: 1.5rem;
  background: ${({ $tier }) => tierColors[$tier].ribbon};
  color: #fff;
  padding: 0.4rem 0.9rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;

const TierName = styled.h3`
  font-size: 1.6rem;
  font-weight: 800;
  color: ${({ $tier }) => tierColors[$tier].accent};
  margin-top: 0.5rem;
  margin-bottom: 0.25rem;
`;

const Amount = styled.div`
  font-size: 1.3rem;
  font-weight: 800;
  color: #0a2a4a;
  margin-bottom: 0.25rem;
`;

const Tickets = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #2d3e56;
  margin-bottom: 1.25rem;
`;

const Perks = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    color: #2d3e56;
    font-size: 0.9rem;
    line-height: 1.4;
    margin-bottom: 0.6rem;
  }

  svg {
    flex-shrink: 0;
    color: #faa71e;
    margin-top: 0.2rem;
  }
`;

const currentSponsors = [
  { img: CabinetProtectiaMunciiLogo, alt: "Cabinet Protecția Muncii" },
  { img: LatinBugLogo, alt: "Latin BUG — Body Used Gently" },
  { img: LoxanLogo, alt: "Loxan" },
  { img: BizzLinkLogo, alt: "BizzLink" },
  { img: PresspectivaLogo, alt: "Presspectiva" },
  { img: UntoldLogo, alt: "UNTOLD" },
  { img: CodruLogo, alt: "Codru Festival" },
  { img: DarcLogo, alt: "D'arc Club" },
  { img: CopyShopLogo, alt: "Copy Shop" },
  { img: ZiuaDeVestLogo, alt: "Ziua de Vest" },
  { img: LitensLogo, alt: "Litens" },
  { img: IuliusLogo, alt: "Iulius Town" },
  { img: AvonLogo, alt: "Avon" },
  { img: EndavaLogo, alt: "Endava" },
];

const SPONSOR_REPEATS = 6;

const SponsorPackages = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const loop = Array.from({ length: SPONSOR_REPEATS }).flatMap(
    () => currentSponsors
  );

  return (
    <Section id="sponsori">
      <Wrap>
        <CurrentSponsorsBlock>
          <Eyebrow>Mulțumim sponsorilor</Eyebrow>
          <ThanksHeading>Sponsorii ediției 2026</ThanksHeading>
          <ThanksSub>
            Companii care au ales să fie alături de Cursa de Rățuște și să
            sprijine direct dotarea Secției de Neonatologie a Spitalului „Louis
            Țurcanu”.
          </ThanksSub>
        </CurrentSponsorsBlock>
      </Wrap>

      <Marquee>
        <Track>
          {loop.map((s, i) => (
            <SponsorCard
              key={`${s.alt}-${i}`}
              aria-hidden={i >= currentSponsors.length}
            >
              <img
                src={s.img}
                alt={i >= currentSponsors.length ? "" : s.alt}
              />
            </SponsorCard>
          ))}
        </Track>
      </Marquee>

      <Wrap>
        <JoinCTA>
          <JoinText>Vrei ca logo-ul tău să fie aici?</JoinText>
          <JoinButton type="button" onClick={() => setModalOpen(true)}>
            Devino sponsor <FiArrowRight size={16} />
          </JoinButton>
        </JoinCTA>

        <Divider style={{ marginTop: "3rem" }} />

        <Eyebrow>Pachete de parteneriat</Eyebrow>
        <Heading>Devino sponsorul ediției 2026</Heading>
        <Sub>
          Companiile care se alătură Cursei de Rățuște primesc vizibilitate
          națională și sprijină direct dotarea Secției de Neonatologie a
          Spitalului „Louis Țurcanu”. Alege pachetul potrivit pentru tine.
        </Sub>
        <Grid>
          {PackagesData.map((pkg) => (
            <Card
              key={pkg.tier}
              $tier={pkg.tier}
              data-aos="fade-up"
              data-aos-duration="600"
            >
              <Ribbon $tier={pkg.tier}>{pkg.name}</Ribbon>
              <TierName $tier={pkg.tier}>{pkg.name}</TierName>
              <Amount>{pkg.amount}</Amount>
              <Tickets>{pkg.tickets}</Tickets>
              <Perks>
                {pkg.perks.map((p, i) => (
                  <li key={i}>
                    <FaCheck size={12} />
                    <span>{p}</span>
                  </li>
                ))}
              </Perks>
            </Card>
          ))}
        </Grid>
      </Wrap>

      <SponsorModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </Section>
  );
};

export default SponsorPackages;
